// ContactEditDesktopTimeline — the desktop right-column (Activity / Public /
// Insights) extracted verbatim from ContactEditModal.tsx's modalContent JSX
// (the `{!isMobile && !freeformMode && (...)}` right column). Tab/close header,
// Insights + Public panels, the expanded-email detail view, and the merged
// activities/Gmail-threads timeline IIFE (with its local TimelineItem/
// ThreadGroup types moved wholesale so the closures stay intact).
//
// The desktop composer is passed in as `composerSlot` (a rendered
// <ContactEditDesktopComposer>) so the load-bearing fragment nesting under
// `rightTab === 'activity'` is preserved while the composer lives in its own
// file. No `fetch` here — the timeline is pure render; data loaders (insights/
// public) and the history backfill are delegated to props.
//
// Markup/handlers/inline styles/a11y are byte-identical to the inline original.
import React from 'react';
import DOMPurify from 'dompurify';
import { ContactConvoIdentityBar } from '../../components/ContactConvoIdentityBar';
import { AttachmentPreview } from '../AttachmentPreview';
import { activityIconConfig, activityIcon } from '../constants';
import { stripHtmlToText, formatNoteWithStyledLabels } from '../utils';
import { determineEmailDirection, isAutomatedSender } from '../lib/contactTimeline';
import type { Activity } from '../lib/types';

type GmailAttachment = { filename: string; mimeType: string; size?: number; attachmentId: string };

interface GmailMessageLike {
  id: string;
  from: string;
  to: string;
  date: string;
  body: string;
  subject: string;
  threadId: string;
  attachments?: GmailAttachment[];
}

type ExpandedEmail = { id: string; from: string; to: string; date: string; body: string; subject: string; attachments?: GmailAttachment[] } | null;

type EmailInsights = {
  tone?: string; dynamic?: string; topics?: string[];
  trajectory?: string; openLoops?: string[]; summary?: string;
} | null;

type PublicActivity = Array<{ date?: string; text: string; sourceUrl?: string }> | null;

export interface ContactEditDesktopTimelineProps {
  onClose: () => void;
  viewOnly: boolean;
  rightTab: 'activity' | 'public' | 'insights';
  emailInsights: EmailInsights;
  emailInsightsLoading: boolean;
  publicActivity: PublicActivity;
  publicActivityLoading: boolean;
  onTabSelect: (tab: 'activity' | 'public' | 'insights') => void;
  expandedEmail: ExpandedEmail;
  setExpandedEmail: (e: ExpandedEmail) => void;
  refetchGmail: () => void;
  gmailMessages: GmailMessageLike[];
  gmailSearching: boolean;
  gmailLoadAll: boolean;
  setGmailLoadAll: (b: boolean) => void;
  activities: Activity[];
  email: string;
  profile: { id?: string; email?: string } | null | undefined;
  gmailAccountEmails: string[];
  historySynced: boolean;
  syncingHistory: boolean;
  syncFullHistory: () => void;
  pendingSendId: string | null;
  sentMessageIds: Set<string>;
  editingNoteId: string | null;
  editingNoteText: string;
  setEditingNoteText: (s: string) => void;
  setEditingNoteId: (s: string | null) => void;
  setActivities: React.Dispatch<React.SetStateAction<Activity[]>>;
  isEditingNoteRef: React.MutableRefObject<boolean>;
  handleSaveNote: () => void;
  handleCancelEdit: () => void;
  handleEditNote: (activity: Activity) => void;
  handleDeleteNote: (id: string) => void;
  composerSlot: React.ReactNode;
}

export function ContactEditDesktopTimeline({
  onClose, viewOnly, rightTab, emailInsights, emailInsightsLoading,
  publicActivity, publicActivityLoading, onTabSelect, expandedEmail, setExpandedEmail,
  refetchGmail, gmailMessages, gmailSearching, gmailLoadAll, setGmailLoadAll, activities,
  email, profile, gmailAccountEmails, historySynced, syncingHistory, syncFullHistory,
  pendingSendId, sentMessageIds, editingNoteId, editingNoteText, setEditingNoteText,
  setEditingNoteId, setActivities, isEditingNoteRef, handleSaveNote, handleCancelEdit,
  handleEditNote, handleDeleteNote, composerSlot,
}: ContactEditDesktopTimelineProps) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      borderLeft: '1px solid #DADCE0',
      background: 'var(--color-surface)',
      overflow: 'hidden',
      position: 'relative', // scopes the alias-editor modal overlay to this panel
    }}>
      {/* Right column header — Activity / Public tabs + close button.
          alignItems=flex-end so the tab buttons' own border-bottom sits
          flush with the container's border-bottom (= left header line). */}
      <div style={{ padding: '0 20px', borderBottom: '1px solid #DADCE0', flexShrink: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 48, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {(['activity'] as Array<'activity' | 'public' | 'insights'>).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => onTabSelect(tab)}
              style={{
                padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: rightTab === tab ? 600 : 400,
                color: rightTab === tab ? '#1a1a1a' : '#7a7f85',
                borderBottom: rightTab === tab ? '2px solid var(--color-brand)' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              {tab === 'activity' ? 'Activity' : tab === 'public' ? 'Public' : 'Insights'}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: 32, height: 32, borderRadius: 6, border: 'none',
            background: 'none', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)',
            alignSelf: 'center',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F3F4'; e.currentTarget.style.color = '#1a1a1a'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#7a7f85'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Email insights panel */}
      {rightTab === 'insights' && (
        <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {emailInsightsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: 'var(--color-text-muted)', fontSize: 13 }}>
              Analyzing email history...
            </div>
          ) : emailInsights && emailInsights.summary ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Summary */}
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--color-text)' }}>
                {emailInsights.summary}
              </div>

              {/* Tone & Dynamic */}
              <div style={{ display: 'flex', gap: 8 }}>
                {emailInsights.tone && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: 'var(--xavior-blue-bg, #f0f9ff)', color: 'var(--color-brand)', border: '1px solid #bae6fd',
                  }}>
                    {emailInsights.tone}
                  </span>
                )}
                {emailInsights.trajectory && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 12, fontSize: 12,
                    background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                  }}>
                    {emailInsights.trajectory}
                  </span>
                )}
              </div>

              {/* Dynamic */}
              {emailInsights.dynamic && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Dynamic</div>
                  <div style={{ fontSize: 13, color: '#4a4d51', lineHeight: 1.5 }}>{emailInsights.dynamic}</div>
                </div>
              )}

              {/* Topics */}
              {emailInsights.topics && emailInsights.topics.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Key Topics</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {emailInsights.topics.map((topic, i) => (
                      <span key={i} style={{
                        padding: '3px 10px', borderRadius: 10, fontSize: 12,
                        background: '#F1F3F4', color: '#4a4d51', border: '1px solid #E8E9EC',
                      }}>
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Open Loops */}
              {emailInsights.openLoops && emailInsights.openLoops.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Open Threads</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {emailInsights.openLoops.map((item, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#4a4d51', lineHeight: 1.4, paddingLeft: 10, borderLeft: '2px solid #fbbf24' }}>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : gmailMessages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-muted)' }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>No emails to analyze</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Email history is needed to generate insights.</div>
            </div>
          ) : emailInsights !== null ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-muted)' }}>
              <div style={{ fontSize: 13 }}>Could not generate insights.</div>
            </div>
          ) : null}
        </div>
      )}

      {/* Public activity panel */}
      {rightTab === 'public' && (
        <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {publicActivityLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: 'var(--color-text-muted)', fontSize: 13 }}>
              Searching for recent activity...
            </div>
          ) : publicActivity && publicActivity.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {publicActivity.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--color-text-faint)', minWidth: 70, flexShrink: 0, paddingTop: 2 }}>
                    {item.date || ''}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.5 }}>
                      {item.text}
                    </div>
                    {item.sourceUrl && (
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 11, color: 'var(--color-text-muted)', textDecoration: 'none' }}
                        onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--color-brand)'; }}
                        onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#7a7f85'; }}
                      >
                        {(() => { try { return new URL(item.sourceUrl).hostname.replace('www.', ''); } catch { return 'source'; } })()} ↗
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : publicActivity !== null ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-muted)' }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>No recent public activity found</div>
              <div style={{ fontSize: 12 }}>Try adding an X handle or company to improve results.</div>
            </div>
          ) : null}
        </div>
      )}

      {/* Expanded email detail view / Activity feed — only when activity tab */}
      {rightTab === 'activity' && (<>{expandedEmail ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #DADCE0', flexShrink: 0 }}>
            <div
              onClick={() => setExpandedEmail(null)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a9ba5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#1a9ba5' }}>Back to Activity</span>
            </div>
          </div>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #DADCE0', flexShrink: 0 }}>
            {expandedEmail.subject && (
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 }}>
                {expandedEmail.subject}
              </div>
            )}
            <div style={{ fontSize: 12, color: '#6b6560', lineHeight: 1.6 }}>
              <div><strong>From:</strong> {expandedEmail.from}</div>
              <div><strong>To:</strong> {expandedEmail.to}</div>
              <div><strong>Date:</strong> {new Date(expandedEmail.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} {new Date(expandedEmail.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', minHeight: 0 }}>
            {expandedEmail.body.includes('<') ? (
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><style>body{font-family:'DM Sans',system-ui,sans-serif;font-size:14px;line-height:1.6;color:#1a1a1a;margin:0;padding:0;word-break:break-word;}a{color:#1a9ba5;}img{max-width:100%;height:auto;}</style></head><body>${DOMPurify.sanitize(expandedEmail.body)}</body></html>`}
                style={{ width: '100%', minHeight: 120, maxHeight: expandedEmail.attachments?.length ? 300 : '100%', border: 'none' }}
                sandbox="allow-same-origin"
                title="Email content"
              />
            ) : (
              <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--color-text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {expandedEmail.body}
              </div>
            )}
            {/* Attachments */}
            {expandedEmail.attachments && expandedEmail.attachments.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #DADCE0', paddingTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                  Attachments ({expandedEmail.attachments.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {expandedEmail.attachments.map((att, i) => {
                    const isImage = att.mimeType.startsWith('image/');
                    return (
                      <AttachmentPreview key={`${expandedEmail.id}-${att.attachmentId}-${i}`} att={att} messageId={expandedEmail.id} isImage={isImage} />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (<>
      {/* "You: <connected> · +N more · edit list" — the addresses matched as you
          (so forwarded mail to your other addresses shows here). Refetch on save. */}
      <ContactConvoIdentityBar onChange={() => refetchGmail()} />
      {/* Activity feed - iMessage-style scrollable */}
      <div
        ref={(el) => { if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 50); }}
        style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '12px 16px', display: 'flex', flexDirection: 'column' }}>
        {(() => {
          // Build merged timeline from activities + Gmail threads
          type TimelineItem = {
            id: string;
            date: string;
            kind: 'email-sent' | 'email-received' | 'activity';
            body: string;
            subject?: string;
            type?: string; // activity type
            activity?: Activity; // original activity for editing
            authorName?: string;
            threadId?: string;
            attachments?: GmailAttachment[];
            gmailMessageId?: string;
          };
          type ThreadGroup = { threadId: string; subject: string; date: string; messages: TimelineItem[] };
          type TimelineEntry = { entryType: 'thread'; data: ThreadGroup; date: string } | { entryType: 'activity'; data: TimelineItem; date: string };

          const userEmailList = [...new Set([profile?.email, ...gmailAccountEmails].filter(Boolean))].map(e => e!.toLowerCase());
          const contactEmail = email?.toLowerCase() || '';
          const activityItems: TimelineItem[] = [];

          // Collect Gmail message bodies for deduplication
          const gmailBodyTexts = gmailMessages.map(m => stripHtmlToText(m.body).trim().toLowerCase());

          // Add activities (non-email only when Gmail is present, but keep recent optimistic sends)
          const now = Date.now();
          for (const a of activities) {
            // Treat both true email activities AND notes whose text is an
            // "Email to/from …:" log (older sends were logged as type 'note')
            // as email entries — so they get the prefix stripped, render as
            // chat bubbles, and dedupe against the Gmail thread instead of
            // showing as raw one-sided "Email to X" note rows.
            const isEmailType = a.type === 'email' || /^Email (?:to|from) /i.test(a.note);
            if (isEmailType && gmailMessages.length > 0) {
              // Keep very recent optimistic emails (< 30s) that Gmail hasn't picked up yet
              const ageMs = now - new Date(a.date).getTime();
              const strippedNoteLower = a.note.replace(/^Email (?:to|from) [^:]+:\s*/i, '').trim().toLowerCase();
              const alreadyInGmail = gmailBodyTexts.some(gt => gt.includes(strippedNoteLower) || strippedNoteLower.includes(gt));
              if (ageMs > 30000 || alreadyInGmail) continue;
            }

            const isFromContact = /^Email from /i.test(a.note) || (a.authorId && a.authorId !== profile?.id);
            const strippedNote = a.note.replace(/^Email (?:to|from) [^:]+:\s*/i, '').trim();
            if (isEmailType) {
              activityItems.push({ id: a.id, date: a.date, kind: isFromContact ? 'email-received' : 'email-sent', body: strippedNote, activity: a, authorName: a.authorName });
            } else {
              activityItems.push({ id: a.id, date: a.date, kind: 'activity', body: a.note, type: a.type || 'note', activity: a, authorName: a.authorName });
            }
          }

          // Group Gmail messages by thread
          const threadMap = new Map<string, ThreadGroup>();
          for (const gm of gmailMessages) {
            const strippedBody = stripHtmlToText(gm.body);
            if (strippedBody === '[Calendar Invite]') continue;
            if (!strippedBody.trim()) continue;
            if (isAutomatedSender(gm.from)) continue;
            const fromLower = gm.from.toLowerCase();
            const isSent = determineEmailDirection(fromLower, userEmailList, contactEmail || undefined);
            const tid = gm.threadId || gm.id;
            const msg: TimelineItem = {
              id: `gmail-${gm.id}`, date: gm.date,
              kind: isSent ? 'email-sent' : 'email-received',
              body: strippedBody, subject: gm.subject, threadId: tid,
              attachments: gm.attachments, gmailMessageId: gm.id,
            };
            if (!threadMap.has(tid)) {
              threadMap.set(tid, { threadId: tid, subject: '', date: gm.date, messages: [] });
            }
            threadMap.get(tid)!.messages.push(msg);
          }
          for (const tg of threadMap.values()) {
            tg.messages.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            tg.date = tg.messages[0].date;
            // Pick the longest non-empty subject across the thread
            // (some forwards/replies in the same thread carry better
            // subjects than the message we happened to add first).
            const subjects = tg.messages
              .map(m => (m.subject || '').replace(/^(Re|Fwd):\s*/i, '').trim())
              .filter(Boolean);
            if (subjects.length === 0) {
              // Fall back to any raw subject — even "Re: foo" is more
              // useful than nothing for a divider.
              const raw = tg.messages.map(m => (m.subject || '').trim()).filter(Boolean);
              tg.subject = raw[0] || '';
            } else {
              tg.subject = subjects.reduce((a, b) => (b.length > a.length ? b : a));
            }
          }

          // Combine threads and activity items, sorted by date
          const combined: TimelineEntry[] = [];
          for (const tg of threadMap.values()) combined.push({ entryType: 'thread', data: tg, date: tg.date });
          for (const ai of activityItems) combined.push({ entryType: 'activity', data: ai, date: ai.date });
          combined.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          if (combined.length === 0) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 13, fontStyle: 'italic' }}>
                  {gmailSearching ? 'Searching emails...' : 'No activity yet'}
                </span>
                {!gmailSearching && !gmailLoadAll && email && (
                  <button
                    type="button"
                    onClick={() => setGmailLoadAll(true)}
                    style={{
                      padding: '6px 16px', fontSize: 12, fontWeight: 500,
                      color: 'var(--color-brand)', background: 'none', border: '1px solid #bae6fd',
                      borderRadius: 8, cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--xavior-blue-bg, #f0f9ff)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                  >
                    Search all messages
                  </button>
                )}
              </div>
            );
          }

          // Group by month
          const groups: { key: string; label: string; entries: TimelineEntry[] }[] = [];
          const groupMap = new Map<string, TimelineEntry[]>();
          for (const entry of combined) {
            const d = new Date(entry.date);
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
            if (!groupMap.has(key)) {
              groupMap.set(key, []);
              groups.push({ key, label, entries: groupMap.get(key)! });
            }
            groupMap.get(key)!.push(entry);
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Load earlier button */}
              {!gmailLoadAll && (
                <button
                  type="button"
                  onClick={() => setGmailLoadAll(true)}
                  style={{
                    alignSelf: 'center', margin: '8px 0 12px',
                    padding: '5px 14px', fontSize: 12, fontWeight: 500,
                    color: '#4a4d51', background: '#F1F3F4', border: '1px solid #DADCE0',
                    borderRadius: 12, cursor: 'pointer',
                  }}
                >
                  {gmailMessages.length > 0 ? 'Load earlier messages' : 'Search all messages'}
                </button>
              )}
              {/* Sync full history — backfill mail older than the sync window from the
                  provider. After a successful run it's done, so confirm and hide. */}
              {email && (historySynced ? (
                <div style={{ alignSelf: 'center', margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  ✓ Full history synced
                </div>
              ) : (
                <button
                  type="button"
                  onClick={syncFullHistory}
                  disabled={syncingHistory}
                  style={{
                    alignSelf: 'center', margin: '0 0 12px',
                    padding: '5px 14px', fontSize: 12, fontWeight: 500,
                    color: 'var(--color-brand)', background: 'none', border: '1px solid #bae6fd',
                    borderRadius: 12, cursor: syncingHistory ? 'default' : 'pointer', opacity: syncingHistory ? 0.7 : 1,
                  }}
                >
                  {syncingHistory ? 'Syncing full history…' : 'Sync full history'}
                </button>
              ))}
              {groups.map(group => (
                <div key={group.key} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.05em', marginBottom: 8, textAlign: 'center' }}>{group.label}</div>
                  {group.entries.map(entry => {
                    if (entry.entryType === 'thread') {
                      const thread = entry.data;
                      return (
                        <div key={thread.threadId} style={{ marginBottom: 12 }}>
                          {thread.subject && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 6px' }}>
                              <div style={{ flex: 1, height: 1, background: '#DADCE0' }} />
                              <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500, whiteSpace: 'nowrap', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {thread.subject}
                              </span>
                              <div style={{ flex: 1, height: 1, background: '#DADCE0' }} />
                            </div>
                          )}
                          {thread.messages.map(msg => {
                            const msgDateStr = new Date(msg.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + new Date(msg.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                            const gmailId = msg.id.replace(/^gmail-/, '');
                            const openEmail = () => {
                              const original = gmailMessages.find(gm => gm.id === gmailId);
                              if (original) setExpandedEmail(original);
                            };
                            const hasAttachments = msg.attachments && msg.attachments.length > 0;
                            const attachmentIndicator = hasAttachments ? (
                              <div onClick={openEmail} style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, cursor: 'pointer', fontSize: 12, color: 'var(--color-text-muted)' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#1a9ba5'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#5f6368'; }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                </svg>
                                {msg.attachments!.length === 1 ? msg.attachments![0].filename : `${msg.attachments!.length} attachments`}
                              </div>
                            ) : null;
                            const bubbleStyle = (sent: boolean): React.CSSProperties => ({
                              background: sent ? '#D6E4F7' : '#D3D6DC',
                              color: 'var(--color-text)',
                              padding: '8px 12px',
                              fontSize: 13,
                              lineHeight: 1.5,
                              borderRadius: sent ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              cursor: 'pointer',
                              transition: 'box-shadow 0.12s, transform 0.12s',
                            });
                            if (msg.kind === 'email-sent') {
                              return (
                                <div key={msg.id} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                                  <div style={{ maxWidth: '80%' }}>
                                    <div
                                      onClick={openEmail}
                                      title="Open full email"
                                      style={bubbleStyle(true)}
                                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 1px 6px rgba(26,155,165,0.18)'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                    >
                                      {msg.body}
                                      {attachmentIndicator}
                                    </div>
                                    <div onClick={openEmail} style={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'right', marginTop: 2, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#1a9ba5'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#7a7f85'; }}>{msgDateStr}</div>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div key={msg.id} style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
                                <div style={{ maxWidth: '80%' }}>
                                  <div
                                    onClick={openEmail}
                                    title="Open full email"
                                    style={bubbleStyle(false)}
                                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 1px 6px rgba(26,155,165,0.18)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                                  >
                                    {msg.body}
                                    {attachmentIndicator}
                                  </div>
                                  <div onClick={openEmail} style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2, cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#1a9ba5'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#7a7f85'; }}>{msgDateStr}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // Activity item
                    const item = entry.data;
                    const dateStr = new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + new Date(item.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

                    // Email-type activity (when Gmail data unavailable) — render as chat bubbles
                    if (item.kind === 'email-sent') {
                      const isPending = pendingSendId === item.id;
                      const isSent = sentMessageIds.has(item.id);
                      return (
                        <div key={item.id} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                          <div style={{ maxWidth: '80%' }}>
                            <div style={{ background: '#D6E4F7', color: 'var(--color-text)', padding: '8px 12px', fontSize: 13, lineHeight: 1.5, borderRadius: '14px 14px 4px 14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {item.body}
                            </div>
                            <div style={{ fontSize: 12, color: isPending ? '#16a34a' : '#7a7f85', textAlign: 'right', marginTop: 2 }}>
                              {isPending ? (
                                <span style={{ fontWeight: 500 }}>Sending...</span>
                              ) : (
                                <>{dateStr}{isSent && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 4 }}><polyline points="20 6 9 17 4 12"/></svg>}</>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (item.kind === 'email-received') {
                      return (
                        <div key={item.id} style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                          <div style={{ maxWidth: '80%' }}>
                            <div style={{ background: '#D3D6DC', color: 'var(--color-text)', padding: '8px 12px', fontSize: 13, lineHeight: 1.5, borderRadius: '14px 14px 14px 4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {item.body}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'left', marginTop: 2 }}>{dateStr}</div>
                          </div>
                        </div>
                      );
                    }

                    const actType = item.type || 'note';
                    const iconCfg = activityIconConfig[actType] || activityIconConfig.note;
                    const activity = item.activity;
                    return (
                      <div key={item.id} className="group" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                        <div style={{
                          background: '#FEF3C7', borderRadius: '14px 14px 4px 14px', padding: '6px 12px', maxWidth: '80%',
                          display: 'flex', alignItems: 'flex-start', gap: 8,
                        }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%',
                            background: iconCfg.bg, color: iconCfg.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, marginTop: 1,
                          }}>
                            {activityIcon(actType)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {activity && editingNoteId === activity.id ? (
                              <textarea
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                onBlur={(e) => {
                                  const relatedTarget = e.relatedTarget as HTMLButtonElement;
                                  if (relatedTarget?.type === 'submit') {
                                    if (!editingNoteId) return;
                                    const trimmed = editingNoteText.trim();
                                    if (trimmed) {
                                      const updatedActivities = activities.map(a => a.id === editingNoteId ? { ...a, note: trimmed } : a);
                                      setActivities(updatedActivities);
                                    }
                                    setEditingNoteId(null); setEditingNoteText('');
                                    setTimeout(() => { isEditingNoteRef.current = false; }, 100);
                                    return;
                                  }
                                  handleSaveNote();
                                }}
                                style={{ width: '100%', padding: '4px 8px', fontSize: 13, border: '2px solid var(--color-brand)', borderRadius: 6, resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
                                rows={Math.max(1, editingNoteText.split('\n').length)}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveNote(); }
                                  else if (e.key === 'Escape') { e.preventDefault(); handleCancelEdit(); }
                                }}
                              />
                            ) : (
                              <>
                                <div style={{ fontSize: 13, color: 'var(--color-text)', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                                  {formatNoteWithStyledLabels(item.body)}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                    {dateStr}
                                  </span>
                                  {!viewOnly && activity && (
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ display: 'flex', gap: 4 }}>
                                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditNote(activity); }} onMouseDown={(e) => e.stopPropagation()}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-muted)' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-brand)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = '#7a7f85'; }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                      </button>
                                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteNote(activity.id); }} onMouseDown={(e) => e.stopPropagation()}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-muted)', fontSize: 14, lineHeight: 1 }}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = '#c0392b'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = '#7a7f85'; }}
                                      >
                                        x
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Composer — two tabs: Send Email / Internal Note */}
      {!viewOnly && composerSlot}
      </>)}</>)}
    </div>
  );
}
